import { useFlag } from '@calendly/feature-check-react'
import { useCallback, useMemo } from 'react'

import { useGetOrganizationUuid } from 'common'
import { buildFilters } from 'components/meetings/filter_modal/utils'
import { useFilters } from 'hooks/meetings/filters'
import { useScope } from 'hooks/meetings/use_scope'
import { WEEK_STARTS_ON_MONDAY } from 'services/iso_week_start'
import {
  useLazyGetMeetingsFilterCountQuery,
  useLazySearchMeetingsSupergraphFilterCountQuery,
} from 'shared/api/meetings'
import { useCurrentTime } from 'shared/contexts'

import { useLookupUuidsQuery } from '../api'
import { buildSupergraphCountRequest } from './search_data/supergraph_meeting_search_utils'
import { useSelectedView } from './use_selected_view'
import { allowedScopeTypes, Periods } from '../../../constants/meetings'
import { mapScopeTypeToBackendValue } from '../utils'

/** Week bounds depend on where the week starts; meetings uses Monday–Sunday. */
const WEEK_PERIODS = new Set([Periods.THIS_WEEK, Periods.LAST_WEEK])

const buildMeetingsFilterCountVariables = ({
  filterParams,
  scopeType,
  scopeId,
  selectedView
}) => {
  const filters = buildFilters(
    scopeType,
    scopeId,
    selectedView,
    filterParams,
    true,
    allowedScopeTypes,
    mapScopeTypeToBackendValue
  )

  return {
    forCount: true,
    ...filters,
    // Keep counts on the same Monday–Sunday week as the meetings list (SA-4357).
    ...(WEEK_PERIODS.has(filters?.period) ? { weekStartsOn: WEEK_STARTS_ON_MONDAY } : {}),
  }
}

const useFilterParamsWithUuids = (filterParams) => {
  const { data: uuids } = useLookupUuidsQuery({ filters: filterParams })

  if (!uuids) {
    return null
  }

  return {
    ...filterParams,
    ...uuids
  }
}

export const useFetchMeetingsFilterCountForAnalytics = (filterDefaults = {}) => {
  const { getFilterParams } = useFilters()
  const filterParams = getFilterParams(filterDefaults)
  const filtersToUse = useFilterParamsWithUuids(filterParams)
  const { scopeType, scopeId } = useScope()
  const selectedView = useSelectedView()
  const isSupergraphEnabled = useFlag('sa_meetings_page_with_supergraph', false)
  const organizationUuid = useGetOrganizationUuid()
  const [triggerLegacyFilterCount] = useLazyGetMeetingsFilterCountQuery()
  const [triggerSupergraphFilterCount] = useLazySearchMeetingsSupergraphFilterCountQuery()

  return useCallback(async (searchKeyword) => {
    if (!filtersToUse) {
      return 0
    }
    const filterParamsForCount = searchKeyword !== undefined
      ? { ...filtersToUse, search_keyword: searchKeyword }
      : filtersToUse
    try {
      const variables = buildMeetingsFilterCountVariables({
        filterParams: filterParamsForCount,
        scopeType,
        scopeId,
        selectedView
      })
      if (isSupergraphEnabled) {
        if (!organizationUuid) return 0
        const countRequest = buildSupergraphCountRequest({
          ...variables,
          organizationUuid,
        })
        const total = await triggerSupergraphFilterCount(countRequest).unwrap()
        return typeof total === 'number' ? total : 0
      }
      const data = await triggerLegacyFilterCount(variables).unwrap()
      return data?.meetings?.total ?? 0
    } catch {
      return 0
    }
  }, [
    filtersToUse,
    scopeType,
    scopeId,
    selectedView,
    isSupergraphEnabled,
    organizationUuid,
    triggerLegacyFilterCount,
    triggerSupergraphFilterCount,
  ])
}

export const createUseResultsCount = (
  useGetMeetingsFilterCountQuery,
  useSupergraphFilterCountQuery,
) => (filterParams, skip) => {
  const filtersToUse = useFilterParamsWithUuids(filterParams)
  const { scopeType, scopeId } = useScope()
  const selectedView = useSelectedView()
  const isSupergraphEnabled = useFlag('sa_meetings_page_with_supergraph', false)
  const organizationUuid = useGetOrganizationUuid()
  const currentTime = useCurrentTime()
  useMemo(
    () => currentTime.format('YYYY-MM-DD'),
    [currentTime],
  )
  const countVariables = useMemo(
    () => (skip
      ? null
      : buildMeetingsFilterCountVariables({
        filterParams: filtersToUse,
        scopeType,
        scopeId,
        selectedView
      })),
    [skip, filtersToUse, scopeId, scopeType, selectedView]
  )

  const supergraphCountRequest = useMemo(
    () => (skip || !isSupergraphEnabled || !countVariables || !organizationUuid
      ? null
      : buildSupergraphCountRequest({
        ...countVariables,
        organizationUuid,
      })),
    [skip, isSupergraphEnabled, countVariables, organizationUuid],
  )

  const { data: legacyData, isFetching: legacyFetching } = useGetMeetingsFilterCountQuery(
    countVariables ?? {},
    { skip: skip || isSupergraphEnabled },
  )

  const { data: superData, isFetching: superFetching } = useSupergraphFilterCountQuery(
    supergraphCountRequest ?? { path: 'listMeetings', inputs: [] },
    { skip: skip || !isSupergraphEnabled || !supergraphCountRequest },
  )

  if (skip) {
    return {
      count: 0,
      isLoading: false
    }
  }

  let count
  if (isSupergraphEnabled) {
    count = typeof superData === 'number' ? superData : 0
  } else {
    count = legacyData?.meetings?.total || 0
  }

  return {
    count,
    isLoading: isSupergraphEnabled ? superFetching : legacyFetching
  }
}
